import { useQuery } from '@apollo/client'
import { useNavigate } from '@faceup/router'
import { ContentLayout, usePaginator } from '@faceup/ui'
import {
  Input,
  Select,
  type SorterResult,
  Table,
  type TableColumns,
  useMessage,
} from '@faceup/ui-base'
import {
  ChannelType,
  ChargebeeSubscriptionStatus,
  Country,
  HotlineOrderStatus,
  HotlinePhoneNumberType,
  HotlinesOrderByPossibleKeys,
  OrderByKeyword,
} from '@faceup/utils'
import type { ResultOf } from '@graphql-typed-document-node/core'
import { type Key, useState } from 'react'
import { graphql } from '../__generated__'

const query = {
  Hotlines: graphql(`
    query HotlinesQuery(
      $page: Int!
      $rowsPerPage: Int!
      $filters: HotlinesFiltersInput!
      $order: [HotlinesOrderBy]
    ) {
      hotlines(
        page: $page
        rowsPerPage: $rowsPerPage
        filters: $filters
        order: $order
      ) {
        totalCount
        items {
          id
          name
          sourceType
          order {
            id
            status
          }
          institution {
            id
            name
            billing {
              id
              subscriptionStatus
            }
          }
          defaultLanguage
          languages
          isEnabled
          config {
            ... on AutomatedHotlineReportSourceConfiguration {
              id
              phoneNumber
              phoneNumberType
              automatedHotlineCountry: country
            }
            ... on AiHotlineReportSourceConfiguration {
              id
              phoneNumber
              phoneNumberType
              aiHotlineCountry: country
            }
            ... on LiveHotlineReportSourceConfiguration {
              id
              phoneNumber
              liveHotlineCountry: country
            }
          }
        }
      }
    }
  `),
}

const useSort = () => {
  const message = useMessage()

  const [sort, setSort] = useState<{
    key: HotlinesOrderByPossibleKeys
    order: OrderByKeyword
  } | null>(null)

  const canBeSortedBy = (columnKey: Key) => {
    return Object.values(HotlinesOrderByPossibleKeys).includes(
      columnKey as HotlinesOrderByPossibleKeys
    )
  }

  const handleSortChange = (newSort: SorterResult) => {
    if (!newSort || !newSort.columnKey || !newSort.order) {
      setSort(null)
      return
    }

    if (!canBeSortedBy(newSort.columnKey as string)) {
      message.error({
        content: 'Invalid sort key',
      })
      return
    }

    setSort({
      key: newSort.columnKey as HotlinesOrderByPossibleKeys,
      order: newSort.order === 'ascend' ? OrderByKeyword.ASC : OrderByKeyword.DESC,
    })
  }

  return { sort, setSort: handleSortChange, canBeSortedBy }
}

type Filters = {
  institutionName: string
  hotlineType: ChannelType | null
  subscriptionStatus: ChargebeeSubscriptionStatus | null
  orderStatus: HotlineOrderStatus | null
  isEnabled: boolean | null
  country: Country | null
  phoneNumberType: HotlinePhoneNumberType | null
}

const defaultFilter = {
  institutionName: '',
  hotlineType: null,
  subscriptionStatus: null,
  orderStatus: null,
  isEnabled: null,
  country: null,
  phoneNumberType: null,
}

const useFilters = () => {
  const [filters, setFilters] = useState<Filters>(defaultFilter)

  const resetFilters = () => {
    setFilters(defaultFilter)
  }

  return { filters, setFilters, resetFilters }
}

const Hotlines = () => {
  const { rowsPerPage, page, getTablePaginator } = usePaginator()
  const navigate = useNavigate()

  const { sort, setSort, canBeSortedBy } = useSort()
  const { filters, setFilters } = useFilters()

  const { data, loading } = useQuery(query.Hotlines, {
    variables: {
      page,
      rowsPerPage,
      filters,
      order: sort ? [sort] : null,
    },
    fetchPolicy: 'cache-and-network',
  })

  const makeTableRow = (hotline: ResultOf<typeof query.Hotlines>['hotlines']['items'][number]) => ({
    id: hotline.id,
    institution: {
      id: hotline.institution.id,
      name: hotline.institution.name,
    },
    hotlineName: hotline.name,
    orderStatus: hotline.order?.status,
    subscriptionStatus: hotline.institution.billing?.subscriptionStatus ?? '-',
    isEnabled: hotline.isEnabled,
    hotlineType: hotline.sourceType.replace('Hotline', ''),
    phoneNumber:
      hotline.config.__typename === 'AutomatedHotlineReportSourceConfiguration' ||
      hotline.config.__typename === 'AiHotlineReportSourceConfiguration' ||
      hotline.config.__typename === 'LiveHotlineReportSourceConfiguration'
        ? hotline.config.phoneNumber
        : '-',
    phoneNumberType:
      hotline.config.__typename === 'AutomatedHotlineReportSourceConfiguration' ||
      hotline.config.__typename === 'AiHotlineReportSourceConfiguration'
        ? hotline.config.phoneNumberType
        : '-',
    country:
      hotline.config.__typename === 'AutomatedHotlineReportSourceConfiguration'
        ? hotline.config.automatedHotlineCountry
        : hotline.config.__typename === 'AiHotlineReportSourceConfiguration'
          ? hotline.config.aiHotlineCountry
          : hotline.config.__typename === 'LiveHotlineReportSourceConfiguration'
            ? hotline.config.liveHotlineCountry
            : '-',
    languages: hotline.languages.join(', '),
    defaultLanguage: hotline.defaultLanguage,
  })

  const hotlines = data?.hotlines.items ?? []
  const totalCount = data?.hotlines.totalCount ?? 0

  const columnsWithoutSorter: TableColumns<ReturnType<typeof makeTableRow>> = [
    {
      title: 'Client Name',
      key: 'institutionName',
      render: ({ institution }) => institution.name,
      filtered: Boolean(filters.institutionName),
      filterDropdown: () => (
        <Input
          allowClear
          placeholder='Search client name'
          value={filters.institutionName}
          onChange={({ target: { value } }) => {
            setFilters(current => ({
              ...current,
              institutionName: value,
            }))
          }}
        />
      ),
    },
    {
      title: 'Subscription status',
      dataIndex: 'subscriptionStatus',
      key: 'subscriptionStatus',
      filtered: Boolean(filters.subscriptionStatus),
      filterDropdown: () => (
        <Select
          allowClear
          placeholder='Select subscription status'
          options={Object.values(ChargebeeSubscriptionStatus).map(status => ({
            label: status,
            value: status,
          }))}
          onChange={value => {
            setFilters(current => ({
              ...current,
              subscriptionStatus: value ?? null,
            }))
          }}
        />
      ),
    },
    {
      title: 'Hotline name',
      dataIndex: 'hotlineName',
      key: 'hotlineName',
    },
    {
      title: 'Hotline type',
      dataIndex: 'hotlineType',
      key: 'hotlineType',
      filtered: Boolean(filters.hotlineType),
      filterDropdown: () => (
        <Select
          allowClear
          placeholder='Select hotline type'
          options={[
            { label: 'Automated Hotline', value: ChannelType.AutomatedHotline },
            { label: 'Live Hotline', value: ChannelType.LiveHotline },
            { label: 'AI Hotline', value: ChannelType.AiHotline },
          ]}
          onChange={value => {
            setFilters(current => ({
              ...current,
              hotlineType: value ?? null,
            }))
          }}
        />
      ),
    },
    {
      title: 'Number type',
      dataIndex: 'phoneNumberType',
      key: 'phoneNumberType',
      filtered: Boolean(filters.phoneNumberType),
      filterDropdown: () => (
        <Select
          allowClear
          placeholder='Select number type'
          options={Object.values(HotlinePhoneNumberType).map(status => ({
            label: status,
            value: status,
          }))}
          onChange={value => {
            setFilters(current => ({
              ...current,
              phoneNumberType: value ?? null,
            }))
          }}
        />
      ),
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      filtered: Boolean(filters.country),
      filterDropdown: () => (
        <Select
          showSearch
          allowClear
          placeholder='Select country'
          options={Object.values(Country).map(country => ({
            label: country,
            value: country,
          }))}
          onChange={value => {
            setFilters(current => ({
              ...current,
              country: value ?? null,
            }))
          }}
        />
      ),
    },
    {
      title: 'Languages',
      dataIndex: 'languages',
      key: 'languages',
    },
    {
      title: 'Hotline status',
      key: 'isEnabled',
      render: ({ isEnabled }) => <>{isEnabled ? 'Active' : 'Inactive'}</>,
      filtered: Boolean(filters.isEnabled),
      filterDropdown: () => (
        <Select
          allowClear
          placeholder='Select hotline status'
          options={[
            { label: 'Active', value: true },
            { label: 'Inactive', value: false },
          ]}
          onChange={value => {
            setFilters(current => ({
              ...current,
              isEnabled: value ?? null,
            }))
          }}
        />
      ),
    },
    {
      title: 'Order status',
      dataIndex: 'orderStatus',
      key: 'orderStatus',
      filtered: Boolean(filters.orderStatus),
      filterDropdown: () => (
        <Select
          allowClear
          placeholder='Select order status'
          options={Object.values(HotlineOrderStatus).map(status => ({
            label: status,
            value: status,
          }))}
          onChange={value => {
            setFilters(current => ({
              ...current,
              orderStatus: value ?? null,
            }))
          }}
        />
      ),
    },
    {
      title: 'Phone number',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
    },
  ]

  const columns: TableColumns<ReturnType<typeof makeTableRow>> = columnsWithoutSorter.map(
    column => ({
      ...column,
      sorter: column.key ? canBeSortedBy(column.key) : false,
    })
  )

  return (
    <ContentLayout header={<ContentLayout.Header title='Hotlines' />}>
      <Table
        shadow
        loading={loading}
        rowKey='id'
        dataSource={hotlines.map(reportSource => makeTableRow(reportSource))}
        locale={{ emptyText: ' ' }}
        scroll={{ x: 'max-content' }}
        style={{ minHeight: 300 }}
        onChange={(_, __, sortChange) => setSort(sortChange as SorterResult)}
        onRow={record => ({
          onClick: () => navigate(routes => routes.institution({ id: record.institution.id })),
        })}
        columns={columns}
        pagination={getTablePaginator({
          totalRows: totalCount,
        })}
      />
    </ContentLayout>
  )
}

export default Hotlines
